var _jsxFileName = "/opt/atlassian/pipelines/agent/build/src/components/register/steps/RegisterPersonalInfo.tsx";
import React, { memo, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { LocalDate } from '@js-joda/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CountryBottomSheetModal, Footer, Form, Header, LogoActionBar, Main, ScrollContainer, SelectorInput } from 'Components/utility';
import { BirthdayInput } from 'Components/utility/BirthdayInput';
import { GenderPicker } from 'Components/utility/GenderPicker';
import { Input } from 'Components/utility/Input';
import { REGISTER } from 'Reducers/register/types';
import { getGenderDisplayText, validateFrenchZipCode } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import log from 'Utilities/log';
import { store } from 'Utilities/store';
const mapStateToProps = ({
  config: {
    adultAges,
    countries
  }
}) => {
  return {
    adultAges,
    countries
  };
};
const connector = connect(mapStateToProps);
const RegisterPersonalInfo = ({
  back,
  validateNameLength,
  validateIsMinor,
  validateDOBAndDisplayError,
  handleClickSignIn,
  showFrenchZipCodeError,
  values: currentValues,
  adultAges,
  currentStep,
  countries
}) => {
  const [isGenderPickerVisible, setIsGenderPickerVisible] = useState(false);
  const [isPlaceOfBirthBottomSheetModalOpen, setIsPlaceOfBirthBottomSheetModalOpen] = useState(false);
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.errors.required')).trim(),
    lastName: Yup.string().required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.errors.required')).trim(),
    dateOfBirth: Yup.string().required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.dob.errors.required')),
    ...(currentValues.country === 'FR' && {
      gender: Yup.string().required(),
      birthCountry: Yup.string().required(),
      birthZipCode: Yup.string().when('birthCountry', {
        is: 'FR',
        then: Yup.string().required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.errors.required')),
        otherwise: Yup.string().notRequired()
      })
    })
  });
  const onSubmit = useCallback(async values => {
    try {
      const isValidNameLength = validateNameLength(values.firstName, values.lastName);
      if (!isValidNameLength) {
        return;
      }
      const isFrench = values.country === 'FR';
      const isBirthInFrance = values.birthCountry === 'FR';
      if (isFrench && isBirthInFrance) {
        const isZipValid = validateFrenchZipCode(values.birthZipCode);
        if (!isZipValid) {
          showFrenchZipCodeError();
          return;
        }
      }
      const register = {
        ...currentValues,
        ...values
      };
      const isMinor = validateIsMinor(values.dateOfBirth, adultAges[values.country.toUpperCase()]);
      store.dispatch({
        type: REGISTER,
        register: {
          ...register,
          isMinor,
          currentStep: currentStep + 1
        }
      });
    } catch (err) {
      log.error(err);
    }
  }, [validateNameLength, currentValues, validateIsMinor, adultAges, currentStep, showFrenchZipCodeError]);
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isValid,
    errors,
    touched,
    values
  } = useFormik({
    initialValues: {
      ...currentValues
    },
    validationSchema,
    onSubmit,
    validateOnBlur: true
  });
  const handleOnBack = useCallback(() => {
    back({
      ...values
    });
  }, [back, values]);
  return /*#__PURE__*/React.createElement(Main, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 168,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement(LogoActionBar, {
    actionButtonTestID: "RegisterPersonalInfo.action-go-back",
    titleTestID: "RegisterPersonalInfo.actionTitle",
    title: i18n.t('CreateAccountPersonalInfo.title'),
    onBack: handleOnBack,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 169,
      columnNumber: 7
    }
  }), /*#__PURE__*/React.createElement(ScrollContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 176,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(Header, {
    titleTestID: "RegisterPersonalInfo.title",
    subtitleTestID: "RegisterPersonalInfo.subtitle",
    title: i18n.t('CreateAccountPersonalInfo.title'),
    subtitle: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.primaryText'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 177,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(Form, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 185,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.label'),
    placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.placeholder'),
    label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.label'),
    testID: "firstName",
    inputMode: "text",
    errorMessage: errors.firstName,
    isInvalid: errors.firstName && touched.firstName ? true : false,
    value: values.firstName,
    onChangeText: handleChange('firstName'),
    onBlur: handleBlur('firstName'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 186,
      columnNumber: 11
    }
  }), /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.label'),
    placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.placeholder'),
    label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.label'),
    testID: "lastName",
    inputMode: "text",
    errorMessage: errors.lastName,
    isInvalid: errors.lastName && touched.lastName ? true : false,
    value: values.lastName,
    onChangeText: handleChange('lastName'),
    onBlur: handleBlur('lastName'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 204,
      columnNumber: 11
    }
  }), /*#__PURE__*/React.createElement(BirthdayInput, {
    error: errors.dateOfBirth && touched.dateOfBirth ? true : false,
    errorMessage: errors.dateOfBirth,
    testID: "dateOfBirth",
    isTouched: touched.dateOfBirth,
    initialDate: values.dateOfBirth ? LocalDate.parse(values.dateOfBirth) : undefined,
    onDateChange: date => {
      const dateString = date.toString();
      const isValid = validateDOBAndDisplayError(dateString);
      if (isValid) {
        setFieldTouched('dateOfBirth', true);
        setFieldValue('dateOfBirth', dateString);
      }
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 222,
      columnNumber: 11
    }
  }), currentValues.country === 'FR' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SelectorInput, {
    testID: "gender",
    label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.gender.label'),
    placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.gender.placeholder'),
    "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.gender.label'),
    value: getGenderDisplayText(values.gender),
    onClick: () => setIsGenderPickerVisible(true),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 240,
      columnNumber: 15
    }
  }, () => {
    return /*#__PURE__*/React.createElement(GenderPicker, {
      isOpen: isGenderPickerVisible,
      selectedGender: values.gender,
      onDone: gender => {
        setIsGenderPickerVisible(false);
        setFieldValue('gender', gender);
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 256,
        columnNumber: 21
      }
    });
  }), /*#__PURE__*/React.createElement(SelectorInput, {
    testID: "birthCountry",
    label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.label'),
    placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.placeholder'),
    "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.label'),
    value: values.birthCountry ? i18n.t(`Global.microcopy.countries.${values.birthCountry.toUpperCase()}`) : '',
    error: errors.birthCountry ? true : false,
    errorMessage: errors.birthCountry,
    isTouched: touched.birthCountry,
    onClick: () => setIsPlaceOfBirthBottomSheetModalOpen(true),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 267,
      columnNumber: 15
    }
  }, () => {
    return /*#__PURE__*/React.createElement(CountryBottomSheetModal, {
      title: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.label'),
      isOpen: isPlaceOfBirthBottomSheetModalOpen,
      onCancel: () => {
        setFieldTouched('birthCountry');
        setIsPlaceOfBirthBottomSheetModalOpen(false);
      },
      onDone: country => {
        if (country !== 'FR') {
          setFieldValue('birthZipCode', '');
        }
        setFieldValue('birthCountry', country);
        setIsPlaceOfBirthBottomSheetModalOpen(false);
      },
      selectedCountry: values.birthCountry,
      countries: countries,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 292,
        columnNumber: 21
      }
    });
  }), values.birthCountry === 'FR' && /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.label'),
    placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.placeholder'),
    label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.label'),
    testID: "birthZipCode",
    inputMode: "numeric",
    errorMessage: errors.birthZipCode,
    isInvalid: errors.birthZipCode && touched.birthZipCode ? true : false,
    value: values.birthZipCode,
    onChangeText: handleChange('birthZipCode'),
    onBlur: handleBlur('birthZipCode'),
    maxLength: 5,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 317,
      columnNumber: 17
    }
  })))), /*#__PURE__*/React.createElement(Footer, {
    buttonText: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'),
    buttonTestID: "RegisterPersonalInfo.nextButton",
    buttonLinkTitle: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.primaryText'),
    buttonLinkTitleTestID: "RegisterPersonalInfo.alreadyHaveAccount",
    buttonLinkText: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label'),
    buttonLinkTestID: "RegisterPersonalInfo.signIn",
    onButtonSubmit: handleSubmit,
    onButtonLinkClick: handleClickSignIn,
    isButtonDisabled: !values.firstName || !values.lastName || !values.dateOfBirth || !isValid,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 341,
      columnNumber: 7
    }
  }));
};
export default connector(/*#__PURE__*/memo(RegisterPersonalInfo));